import { useSignupPageUpdate } from '@/services/onboardingService'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { SIGNUP_PATHS } from '../helpers'
import { TextColors, TypographyStyles } from '@/styles'
import {
  Badge,
  Button,
  Card,
  CardColors,
  Icon,
  Image,
  Link,
  List,
  Text,
  Tooltip,
} from '@/ui'
import { PlanDetail } from '@/features/user/signup/choose-plan/helpers'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { FETCH_PRODUCT_PERMISSIONS_KEY } from '@/actions/permissionActions'
import { FETCH_STRIPE_PRODUCTS_KEY } from '@/actions/settings/billingActions'
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Loader } from '@/base'
import { useAnalyticsTrack } from '@/v2-features/amplitude'

export const TierSelection = ({
  value,
  imageUrl,
  imageAlt,
  title,
  subtitle,
  selection,
  setSelection,
  isStarterPlan = false,
  details,
}: {
  value: string
  imageUrl: string
  imageAlt: string
  title: string
  subtitle: string
  selection: string | undefined
  setSelection: (val: string) => void
  isStarterPlan?: boolean
  details:
    | { title?: string; isVisible: boolean; details: PlanDetail[] }[]
    | null
}) => {
  const isSelected = selection === value
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  useSignupPageUpdate(SIGNUP_PATHS.practiceJourney)
  const isLoadingPermissions = useReselector(selectIsFetchingForKeys, [
    FETCH_PRODUCT_PERMISSIONS_KEY,
    FETCH_STRIPE_PRODUCTS_KEY,
  ])
  const track = useAnalyticsTrack()

  return (
    <Card
      color={CardColors.LEVEL_1}
      className="flex min-h-[620px] pt-6 px-10 flex-col gap-6"
    >
      <div className="flex mt-6 ">
        <Image src={imageUrl} className="h-[200px]" alt={imageAlt} />
      </div>
      <Loader loading={isLoadingPermissions} />

      <Text textAlign="left" variant={TypographyStyles.H1}>
        {title}
      </Text>

      <Text textAlign="left" variant={TypographyStyles.BODY_SM}>
        {subtitle}
      </Text>

      {!isStarterPlan && (
        <Badge className="bg-green-25 p-2">
          <Icon icon={solid('star')} color="text-green-100" />
          <Text color={TextColors.BRAND} variant={TypographyStyles.LABEL_SM}>
            Everything in Starter Plan plus:
          </Text>
        </Badge>
      )}

      {details?.map(
        (d) =>
          d.isVisible && (
            <>
              <Text variant={TypographyStyles.DISPLAY_XXS}>{d.title}</Text>
              <List className="my-0 mt-[-1em] flex flex-col gap-1">
                {d.details.map((detail) => (
                  <List.Item
                    key={detail.text}
                    className="ml-2 flex items-center gap-1"
                  >
                    <Icon icon={regular('check')} color="text-green-50" />
                    <Tooltip>
                      <Tooltip.Trigger>
                        <Link
                          onClick={(e) => e.preventDefault()}
                          variant="secondary"
                          href=""
                        >
                          {detail.text}
                        </Link>
                      </Tooltip.Trigger>
                      <Tooltip.Content>{detail.tooltip}</Tooltip.Content>
                    </Tooltip>
                  </List.Item>
                ))}
              </List>
            </>
          )
      )}

      {!isMobile && <div className="flex-1" />}

      <Button
        variant={isSelected ? 'primary' : 'secondary'}
        onClick={() => {
          setSelection(value)
          track(`clicked ${value} in practice-journey`)
        }}
      >
        {isSelected ? 'Selected' : 'Select'}
      </Button>
    </Card>
  )
}
